// ==========================================================================
// Phone Verification Page - Mobile PWA
// Sends OTP to phone number, then navigates to OTP verification
// ==========================================================================

import CountrySelect from '@/components/auth/CountrySelect';
import { useSendOtp } from '@/hooks/useAuth';
import { config } from '@/lib/config';
import { backgroundImage, weatherIcon, poweredByAtram } from '@/assets';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useNavigate } from 'react-router-dom';

export default function PhoneVerificationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [phone, setPhone] = useState<string | undefined>();
  const [error, setError] = useState('');

  const sendOtpMutation = useSendOtp();

  const handleSubmit = async () => {
    if (!phone || !isValidPhoneNumber(phone)) {
      setError(t('auth.phone.errorInvalidPhone'));
      return;
    }

    setError('');
    // Mock mode for development
    if (config.enableMockApi) {
      navigate('/auth/verify-otp', { state: { phone } });
      return;
    }

    try {
      await sendOtpMutation.mutateAsync({ phone_number: phone });
      // Navigate only after successful mutation
      navigate('/auth/verify-otp', { state: { phone } });
    } catch {
      // All BE errors (including 429 rate limit) shown via global toast
    }
  };

  const isLoading = sendOtpMutation.isPending;

  return (
    <div
      className="min-h-screen flex flex-col bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      {/* Top Text Section */}
      <div className="pt-12 px-6">
        <h1 className="font-display leading-[63px] tracking-[-3px] text-[55px]">
          <span className="font-bold text-black">{t('auth.phone.headline1')}</span>
        </h1>
        <div className="font-display font-light text-[50px] leading-[63px] tracking-[-3px] text-black">
          {t('auth.phone.headline2')}
        </div>
        <div className="font-display font-light text-[50px] leading-[63px] tracking-[-3px] text-black">
          {t('auth.phone.headline3')}
        </div>
      </div>

      {/* Center Weather Icon */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <img
          src={weatherIcon}
          alt="Weather"
          className="w-[140px] h-[140px] object-contain"
        />
      </div>

      {/* Bottom Form Section */}
      <div className="px-6 pb-6">
        <div className="text-[26px] text-black mb-2">{t('auth.phone.title')}</div>
        <p className="text-black/60 text-[16px] mb-5">{t('auth.phone.subtitle')}</p>

        {/* Phone Input */}
        <div className="phone-input-wrapper mb-4">
          <PhoneInput
            defaultCountry="KE"
            countrySelectComponent={CountrySelect}
            value={phone}
            onChange={(value) => {
              setPhone(value);
              setError('');
            }}
            className="flex items-center h-[54px] bg-white rounded-2xl px-4"
          />
        </div>

        {error && <p className="text-red-500 text-[14px] mb-3">{error}</p>}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full h-[54px] bg-brand-primary text-white text-[14px] font-semibold tracking-wider rounded-2xl disabled:opacity-60 mb-6"
        >
          {isLoading ? t('common.loading') : t('auth.phone.submitButton')}
        </button>

        {/* Powered by Atram */}
        <div className="flex items-center justify-center">
          <img
            src={poweredByAtram}
            alt="Powered by Atram"
            className="h-[30px] object-contain"
          />
        </div>
      </div>
    </div>
  );
}
